import { api, screenDeviceRequest } from "./lib/http";
import type {
  CourtBoard,
  AdminTask,
  AdminPlayer,
  AdminDuplicatePlayers,
  AdminPlayerPhoto,
  AdminSuggestions,
  PlayerMergeInput,
  AdminCountry,
  AdminCountryInput,
  AdminCountryState,
  AdminCountryStateInput,
  AdminMcpToken,
  AdminMcpTokenCreated,
  AdminProposal,
  AdminProposalInput,
  AdminBillingPayment,
  AdminBillingSubscription,
  BillingCheckoutInput,
  BillingListFilters,
  BillingPayment,
  BillingPriceOnSale,
  BillingPaymentStatus,
  BillingPrice,
  BillingPriceInput,
  BillingProduct,
  BillingProductInput,
  BillingSubscription,
  BillingSubscriptionStatus,
  ClubSubscriptions,
  ClubSubscription,
  ClubDetail,
  ManualPaymentInput,
  ManualSubscriptionInput,
  PushResult,
  AdminClub,
  AdminClubInput,
  ClubMemberInput,
  ClubMemberInviteResult,
  ClubMembers,
  ClubRole,
  AdminTournament,
  AdminTournamentDetail,
  AdminListingInput,
  AdminTeacher,
  AdminTeacherInput,
  AdminStats,
  ClubDashboard,
  AdminUser,
  ApiResponseList,
  Product,
  CreateProduct,
  UpdateProduct,
  Tournament,
  ClubTournament,
  BracketMatchKey,
  BracketPlace,
  CategoryStructure,
  FirstRoundPlace,
  TournamentSchedule,
  GroupMoveTarget,
  MatchResult,
  PublicTournament,
  Club,
  CreateRegistration,
  Registration,
  Reservation,
  Court,
  CreateCourt,
  ProductCategory,
  CreateProductCategory,
  UpdateProductCategory,
  PointOfSale,
  CreateSaleItem,
  SaleItem,
  Sale,
  Player,
  ClubListPlayer,
  CreatePlayer,
  Category,
  CategoryInput,
  CreateOpenMatch,
  UpdateOpenMatch,
  WebOpenMatch,
  PublicOpenMatch,
  JoinOpenMatch,
  ScreenDetail,
  ScreenFeed,
  ScreenPairingCode,
  ScreenPairingStatus,
  ScreenSummary,
  ScreenTakeoverType,
  Sponsor,
  TournamentDateRange,
  StockProduct,
  RecordedStockMovement,
  StockActivationProduct,
  StockMovement,
  ProductStockConfig,
  StockMovementsRequest,
  Ranking,
} from "@repo/shared-types";
import type { Pagination } from "./lib/pagination";
import {
  ALL_PAGINATION,
  getPagination,
  toUrlSearchParams,
} from "./lib/pagination";

export { setToken, getToken, clearToken } from "./lib/auth";
export {
  apiError,
  type ApiError,
  isSubscriptionRequired,
  ScreenDeviceRequestError,
} from "./lib/http";

export { getPagination };

export const authApi = {
  loginWithGoogle: (id_token: string) => api.post("/auth/google", { id_token }),
  getCurrentUser: () => api.get("/auth/me"),
};

export interface ClubRelations {
  members: number;
  tournaments: number;
  courts: number;
  products: number;
  productCategories: number;
  pointsOfSale: number;
  sales: number;
  players: number;
}

const searchParams = (params: Record<string, string | undefined>) =>
  new URLSearchParams(
    Object.entries(params).filter((entry): entry is [string, string] => entry[1] !== undefined)
  );

export const adminApi = {
  getTasks: () => api.get<AdminTask[]>("/admin/tasks"),
  execTask: (taskId: string) => api.post(`/admin/tasks/exec/${taskId}`),
  getClubs: () => api.get<AdminClub[]>("/admin/clubs"),
  createClub: (data: { name: string }) =>
    api.post<AdminClub>("/admin/clubs", data),
  reorderClubs: (ids: string[]) => api.put("/admin/clubs/order", { ids }),
  getClubById: (id: string) => api.get<AdminClub>(`/admin/clubs/${id}`),
  getClubRelations: (id: string) =>
    api.get<ClubRelations>(`/admin/clubs/${id}/relations`),
  getStats: () => api.get<AdminStats[]>("/admin/stats"),
  updateClubFeatures: (id: string, features: Record<string, boolean>) =>
    api.patch(`/admin/clubs/${id}/features`, features),
  deleteClub: (id: string) => api.delete(`/admin/clubs/${id}`),
  updateClub: (id: string, data: AdminClubInput) =>
    api.patch<AdminClub>(`/admin/clubs/${id}`, data),
  updateClubLogo: (id: string, logo: FormData) =>
    api.post<AdminClub>(`/admin/clubs/${id}/logo`, logo),
  getTournaments: () =>
    api.get<ApiResponseList<AdminTournament>>("/admin/tournaments"),
  getTournamentById: (id: string) =>
    api.get<AdminTournamentDetail>(`/admin/tournaments/${id}`),
  createListing: (data: AdminListingInput) =>
    api.post<AdminTournament>("/admin/tournaments", data),
  updateTournament: (id: string, data: Partial<AdminListingInput>) =>
    api.patch<AdminTournament>(`/admin/tournaments/${id}`, data),
  deleteTournament: (id: string) => api.delete(`/admin/tournaments/${id}`),
  updateTournamentBanner: (id: string, banner: FormData) =>
    api.post<AdminTournament>(`/admin/tournaments/${id}/banner`, banner),
  generateTournamentBanner: (id: string, prompt: string) =>
    api.post<AdminTournament>(`/admin/tournaments/${id}/banner/generate`, {
      prompt,
    }),
  updateTournamentImage: (id: string, image: FormData) =>
    api.post<AdminTournament>(`/admin/tournaments/${id}/image`, image),
  /** The ranking global: official tournaments only. Without a category, the first one with points. */
  getRanking: (categoryId?: string | null) =>
    api.get<Ranking>(
      `/admin/ranking${categoryId ? `?categoryId=${categoryId}` : ""}`
    ),
  getTeachers: () => api.get<ApiResponseList<AdminTeacher>>("/admin/teachers"),
  reorderTeachers: (ids: string[]) => api.put("/admin/teachers/order", { ids }),
  getTeacherById: (id: string) =>
    api.get<AdminTeacher>(`/admin/teachers/${id}`),
  createTeacher: (data: AdminTeacherInput) =>
    api.post<AdminTeacher>("/admin/teachers", data),
  updateTeacher: (id: string, data: Partial<AdminTeacherInput>) =>
    api.patch<AdminTeacher>(`/admin/teachers/${id}`, data),
  deleteTeacher: (id: string) => api.delete(`/admin/teachers/${id}`),
  updateTeacherPhoto: (id: string, photo: FormData) =>
    api.post<AdminTeacher>(`/admin/teachers/${id}/photo`, photo),
  getPlayers: () => api.get<ApiResponseList<AdminPlayer>>("/admin/players"),
  getDuplicatePlayers: () =>
    api.get<ApiResponseList<AdminDuplicatePlayers>>("/admin/players/duplicates"),
  getPlayerById: (id: string) => api.get<AdminPlayer>(`/admin/players/${id}`),
  setPlayerPassword: (id: string, password: string) =>
    api.post<AdminPlayer>(`/admin/players/${id}/password`, { password }),
  sendPlayerPush: (id: string, push: { title: string; body: string }) =>
    api.post<PushResult>(`/admin/players/${id}/push`, push),
  mergePlayers: (input: PlayerMergeInput) =>
    api.post<AdminPlayer>("/admin/players/merge", input),
  getPlayerPhotos: () =>
    api.get<ApiResponseList<AdminPlayerPhoto>>("/admin/player-photos"),
  approvePlayerPhoto: (id: string) => api.post(`/admin/player-photos/${id}/approve`),
  rejectPlayerPhoto: (id: string) => api.post(`/admin/player-photos/${id}/reject`),
  getSuggestions: () => api.get<AdminSuggestions>("/admin/suggestions"),
  reviewSuggestion: (
    kind: "clubs" | "tournaments",
    id: string,
    decision: "accept" | "reject"
  ) => api.post(`/admin/suggestions/${kind}/${id}/${decision}`),
  sendPush: (push: { title: string; body: string }) =>
    api.post<PushResult>("/admin/push", push),
  getCountries: () => api.get<ApiResponseList<AdminCountry>>("/admin/countries"),
  createCountry: (data: AdminCountryInput) =>
    api.post<AdminCountry>("/admin/countries", data),
  updateCountry: (id: string, data: Partial<Omit<AdminCountryInput, "id">>) =>
    api.patch<AdminCountry>(`/admin/countries/${id}`, data),
  deleteCountry: (id: string) => api.delete(`/admin/countries/${id}`),
  reorderCountries: (ids: string[]) => api.put("/admin/countries/order", { ids }),
  getCountryStates: (countryId: string) =>
    api.get<ApiResponseList<AdminCountryState>>(`/admin/countries/${countryId}/states`),
  createCountryState: (countryId: string, data: AdminCountryStateInput) =>
    api.post<AdminCountryState>(`/admin/countries/${countryId}/states`, data),
  updateCountryState: (
    countryId: string,
    stateId: string,
    data: Partial<AdminCountryStateInput>
  ) =>
    api.patch<AdminCountryState>(`/admin/countries/${countryId}/states/${stateId}`, data),
  deleteCountryState: (countryId: string, stateId: string) =>
    api.delete(`/admin/countries/${countryId}/states/${stateId}`),
  reorderCountryStates: (countryId: string, ids: string[]) =>
    api.put(`/admin/countries/${countryId}/states/order`, { ids }),
  getMcpTokens: () => api.get<ApiResponseList<AdminMcpToken>>("/admin/mcp-tokens"),
  createMcpToken: (name: string) =>
    api.post<AdminMcpTokenCreated>("/admin/mcp-tokens", { name }),
  deleteMcpToken: (id: string) => api.delete(`/admin/mcp-tokens/${id}`),
  getBillingProducts: () =>
    api.get<ApiResponseList<BillingProduct>>("/admin/billing/products"),
  createBillingProduct: (data: BillingProductInput) =>
    api.post<BillingProduct>("/admin/billing/products", data),
  updateBillingProduct: (id: string, data: BillingProductInput) =>
    api.patch<BillingProduct>(`/admin/billing/products/${id}`, data),
  archiveBillingProduct: (id: string) => api.delete(`/admin/billing/products/${id}`),
  createBillingPrice: (productId: string, data: BillingPriceInput) =>
    api.post<BillingPrice>(`/admin/billing/products/${productId}/prices`, data),
  archiveBillingPrice: (productId: string, priceId: string) =>
    api.delete(`/admin/billing/products/${productId}/prices/${priceId}`),
  getBillingSubscriptions: (filters: BillingListFilters<BillingSubscriptionStatus>) =>
    api.get<ApiResponseList<AdminBillingSubscription>>(
      `/admin/billing/subscriptions?${searchParams(filters)}`
    ),
  getBillingPayments: (filters: BillingListFilters<BillingPaymentStatus>) =>
    api.get<ApiResponseList<AdminBillingPayment>>(
      `/admin/billing/payments?${searchParams(filters)}`
    ),
  getClubSubscriptions: (clubId: string) =>
    api.get<ClubSubscriptions>(`/admin/clubs/${clubId}/subscription`),
  createManualSubscription: (clubId: string, data: ManualSubscriptionInput) =>
    api.post<BillingSubscription>(`/admin/clubs/${clubId}/subscription`, data),
  updateSubscriptionPeriodEnd: (clubId: string, currentPeriodEnd: string | null) =>
    api.patch<BillingSubscription>(`/admin/clubs/${clubId}/subscription`, { currentPeriodEnd }),
  cancelSubscription: (clubId: string) =>
    api.post<BillingSubscription>(`/admin/clubs/${clubId}/subscription/cancel`),
  getClubPayments: (clubId: string) =>
    api.get<ApiResponseList<BillingPayment>>(`/admin/clubs/${clubId}/payments`),
  registerManualPayment: (clubId: string, data: ManualPaymentInput) =>
    api.post<BillingPayment>(`/admin/clubs/${clubId}/payments`, data),
  getProposals: () => api.get<ApiResponseList<AdminProposal>>("/admin/proposals"),
  getProposal: (id: string) => api.get<AdminProposal>(`/admin/proposals/${id}`),
  createProposal: (data: { clubName: string }) =>
    api.post<AdminProposal>("/admin/proposals", data),
  updateProposal: (id: string, data: AdminProposalInput) =>
    api.patch<AdminProposal>(`/admin/proposals/${id}`, data),
  deleteProposal: (id: string) => api.delete(`/admin/proposals/${id}`),
  updateProposalLogo: (id: string, logo: FormData) =>
    api.post<AdminProposal>(`/admin/proposals/${id}/logo`, logo),
  getCategories: () => api.get<Category[]>("/admin/categories"),
  createCategory: (data: CategoryInput) =>
    api.post<Category>("/admin/categories", data),
  updateCategory: (id: string, data: Partial<CategoryInput>) =>
    api.patch<Category>(`/admin/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/admin/categories/${id}`),
  reorderCategories: (ids: string[]) => api.put("/admin/categories/order", { ids }),
  importCategories: (categories: (CategoryInput & { id?: string })[]) =>
    api.post<{ created: number; updated: number }>(
      "/admin/categories/import",
      { categories }
    ),
  getUsers: () => api.get<AdminUser[]>("/admin/users"),
  getUserById: (id: string) => api.get<AdminUser>(`/admin/users/${id}`),
  addUserToClub: (userId: string, data: { clubId: string; role: string }) =>
    api.post(`/admin/users/${userId}/members`, data),
  removeUserFromClub: (userId: string, memberId: string) =>
    api.delete(`/admin/users/${userId}/members/${memberId}`),
  addClubMember: (clubId: string, data: ClubMemberInput) =>
    api.post<ClubMemberInviteResult>(`/admin/clubs/${clubId}/members`, data),
  updateClubMember: (clubId: string, memberId: string, role: ClubRole) =>
    api.patch(`/admin/clubs/${clubId}/members/${memberId}`, { role }),
  removeClubMember: (clubId: string, memberId: string) =>
    api.delete(`/admin/clubs/${clubId}/members/${memberId}`),
  cancelClubInvitation: (clubId: string, invitationId: string) =>
    api.delete(`/admin/clubs/${clubId}/invitations/${invitationId}`),
};

export const productApi = {
  createProductPos: (product: Omit<CreateProduct, "initialStock" | "trackStock">) =>
    api.post(`/pos/products`, product),
  updateImage: (clubId: string, productId: string, image: FormData) =>
    api.post(`/web-app/club/${clubId}/product/${productId}/image`, image),
  getSales: (clubId: string, pagination: Pagination = ALL_PAGINATION) =>
    api.get<
      ApiResponseList<Sale> & { daySummary: { date: string; amount: number }[] }
    >(`/web-app/club/${clubId}/sale?${toUrlSearchParams(pagination)}`),
  createSale: (clubId: string, saleItems: string[], date?: string) =>
    api.post(`/web-app/club/${clubId}/sale`, { saleItems, date }),
  deleteSaleItems: (clubId: string, saleItems: string[]) =>
    api.delete(
      `/web-app/club/${clubId}/sale/item?saleItems=${saleItems.join(",")}`
    ),
  getSaleItems: (clubId: string) =>
    api.get<ApiResponseList<SaleItem>>(`/web-app/club/${clubId}/sale/item`),
  createSaleItem: (saleItem: CreateSaleItem) =>
    api.post(`/pos/sales`, saleItem),
  createSaleItemWeb: (clubId: string, saleItem: CreateSaleItem) =>
    api.post<ApiResponseList<SaleItem>>(
      `/web-app/club/${clubId}/sale/item`,
      saleItem
    ),
  getCategories: (clubId: string, pagination: Pagination = ALL_PAGINATION) =>
    api.get<ApiResponseList<ProductCategory>>(
      `/web-app/club/${clubId}/product-category?${toUrlSearchParams(
        pagination
      )}`
    ),
  getCategoryById: (clubId: string, categoryId: string) =>
    api.get<ProductCategory>(
      `/web-app/club/${clubId}/product-category/${categoryId}`
    ),
  createCategory: (clubId: string, category: CreateProductCategory) =>
    api.post(`/web-app/club/${clubId}/product-category`, category),
  updateCategory: (
    clubId: string,
    categoryId: string,
    category: UpdateProductCategory
  ) =>
    api.patch(
      `/web-app/club/${clubId}/product-category/${categoryId}`,
      category
    ),
  deleteCategory: (clubId: string, categoryId: string) =>
    api.delete(`/web-app/club/${clubId}/product-category/${categoryId}`),
  updateProduct: (clubId: string, productId: string, product: UpdateProduct) =>
    api.patch(`/web-app/club/${clubId}/product/${productId}`, product),
  getProductById: (clubId: string, productId: string) =>
    api.get<Product>(`/web-app/club/${clubId}/product/${productId}`),
  getAll: (clubId: string, pagination: Pagination = ALL_PAGINATION) =>
    api.get<ApiResponseList<Product>>(
      `/web-app/club/${clubId}/product?${toUrlSearchParams(pagination)}`
    ),
  createProduct: (clubId: string, product: CreateProduct) =>
    api.post(`/web-app/club/${clubId}/product`, product),
  deleteProduct: (clubId: string, productId: string) =>
    api.delete(`/web-app/club/${clubId}/product/${productId}`),
};

export const stockApi = {
  getAll: (clubId: string, pagination: Pagination = ALL_PAGINATION) =>
    api.get<ApiResponseList<StockProduct>>(
      `/web-app/club/${clubId}/stock?${toUrlSearchParams(pagination)}`
    ),
  activate: (clubId: string, products: StockActivationProduct[]) =>
    api.post(`/web-app/club/${clubId}/stock/activate`, { products }),
  deactivate: (clubId: string) =>
    api.post(`/web-app/club/${clubId}/stock/deactivate`),
  configureProduct: (clubId: string, productId: string, config: ProductStockConfig) =>
    api.patch(`/web-app/club/${clubId}/product/${productId}/stock`, config),
  getProductMovements: (
    clubId: string,
    productId: string,
    pagination: Pagination = ALL_PAGINATION
  ) =>
    api.get<ApiResponseList<StockMovement>>(
      `/web-app/club/${clubId}/product/${productId}/stock/movements?${toUrlSearchParams(
        pagination
      )}`
    ),
  recordMovements: (clubId: string, body: StockMovementsRequest) =>
    api.post<{ message: string; movements: RecordedStockMovement[] }>(
      `/web-app/club/${clubId}/stock/movements`,
      body
    ),
};

export type TournamentAvailability = {
  courts: Array<{
    court: Court;
    reservations: Array<{
      id: string;
      start: string;
      end: string;
      type: string;
      phase: string | null;
      tournamentId: string | null;
    }>;
  }>;
  tournamentDateRange: {
    startDate: string;
    endDate: string;
    lastDayExcluded: string;
  };
};

export type CreateTournament = {
  name: string;
  dateRanges: TournamentDateRange[];
  categoryIds: string[];
  groupsBySchedule: boolean;
  groupPhaseRanges: SlotRangeInput[];
  sponsorIds: string[];
};

/** A franja: a court and a block of time. */
export type SlotInput = { courtId: string; start: Date; end: Date };

/** A horario: a time on several courts, which the API cuts into franjas. */
export type SlotRangeInput = { courtIds: string[]; start: Date; end: Date };

export type UpdateTournament = Partial<{
  name: string;
  description: string;
  dateRanges: TournamentDateRange[];
  status: "OPEN_INSCRIPTIONS" | "ACTIVE" | "FINISHED";
  groupsBySchedule: boolean;
  countsForRanking: boolean;
}>;

export const clubApi = {
  getMembers: (clubId: string) =>
    api.get<ClubMembers>(`/web-app/club/${clubId}/members`),
  inviteMember: (clubId: string, data: ClubMemberInput) =>
    api.post<ClubMemberInviteResult>(`/web-app/club/${clubId}/members`, data),
  cancelInvitation: (clubId: string, invitationId: string) =>
    api.delete(`/web-app/club/${clubId}/members/invitations/${invitationId}`),
  // Owner only: a 403 logs out, so check `viewerRole` on the club detail first.
  getSubscription: (clubId: string) =>
    api.get<ClubSubscription>(`/web-app/club/${clubId}/subscription`),
  getSubscriptionPayments: (clubId: string) =>
    api.get<ApiResponseList<BillingPayment>>(`/web-app/club/${clubId}/subscription/payments`),
  getSubscriptionPrices: (clubId: string) =>
    api.get<ApiResponseList<BillingPriceOnSale>>(`/web-app/club/${clubId}/subscription/prices`),
  startSubscriptionCheckout: (clubId: string, data: BillingCheckoutInput) =>
    api.post<{ url: string }>(`/web-app/club/${clubId}/subscription/checkout`, data),
  cancelSubscription: (clubId: string) =>
    api.post<BillingSubscription>(`/web-app/club/${clubId}/subscription/cancel`),
  getStats: (clubId: string) =>
    api.get<AdminStats[]>(`/web-app/club/${clubId}/stats`),
  getDashboard: (clubId: string) =>
    api.get<ClubDashboard>(`/web-app/club/${clubId}/dashboard`),
  /** Without a category, the first one with points. */
  getRanking: (clubId: string, categoryId?: string | null) =>
    api.get<Ranking>(
      `/web-app/club/${clubId}/ranking${categoryId ? `?categoryId=${categoryId}` : ""}`
    ),
  updateLogo: (clubId: string, logo: FormData) =>
    api.post(`/web-app/club/${clubId}/logo`, logo),
  updateTournamentById: (
    clubId: string,
    tournamentId: string,
    data: UpdateTournament
  ) =>
    api.patch<Tournament>(
      `/web-app/club/${clubId}/tournament/${tournamentId}`,
      data
    ),
  addTournamentCategory: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.post(`/web-app/club/${clubId}/tournament/${tournamentId}/category`, {
      categoryId,
    }),
  removeTournamentCategory: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.delete(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}`
    ),
  createRegistration: (
    clubId: string,
    tournamentId: string,
    registration: CreateRegistration
  ) =>
    api.post<Registration>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration`,
      registration
    ),
  updateRegistration: (
    clubId: string,
    tournamentId: string,
    registrationId: string,
    registration: CreateRegistration,
    confirm = false
  ) =>
    api.put<Registration>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration/${registrationId}`,
      { ...registration, confirm }
    ),
  updateRegistrationTimeInterests: (
    clubId: string,
    tournamentId: string,
    registrationId: string,
    timeInterests: string[]
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration/${registrationId}/time-interests`,
      { timeInterests }
    ),
  // confirmed: null takes the registration back to pending.
  reviewRegistration: (
    clubId: string,
    tournamentId: string,
    registrationId: string,
    confirmed: boolean | null,
    confirm = false
  ) =>
    api.patch(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration/${registrationId}`,
      { confirmed, confirm }
    ),
  removeRegistration: (
    clubId: string,
    tournamentId: string,
    registrationId: string,
    confirm = false
  ) =>
    api.delete(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration/${registrationId}`,
      { confirm }
    ),
  getById: (id: string) => api.get<ClubDetail>(`/web-app/club/${id}`),
  getAll: () => api.get("/web-app/club"),
  update: (id: string, data: Partial<Club>) =>
    api.patch(`/web-app/club/${id}`, data),
  createTournament: (clubId: string, tournament: CreateTournament) =>
    api.post<Tournament>(`/web-app/club/${clubId}/tournament`, tournament),
  getTournaments: (clubId: string) =>
    api.get<{ results: Tournament[]; count: number }>(
      `/web-app/club/${clubId}/tournament`
    ),
  getTournamentById: (clubId: string, tournamentId: string) =>
    api.get<ClubTournament>(
      `/web-app/club/${clubId}/tournament/${tournamentId}`
    ),
  deleteTournament: (clubId: string, tournamentId: string) =>
    api.delete(`/web-app/club/${clubId}/tournament/${tournamentId}`),
  createTournamentGroup: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/group`
    ),
  deleteTournamentGroup: (
    clubId: string,
    tournamentId: string,
    groupId: string,
    confirm = false
  ) =>
    api.delete<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group/${groupId}`,
      { confirm }
    ),
  moveRegistration: (
    clubId: string,
    tournamentId: string,
    registrationId: string,
    target: GroupMoveTarget,
    confirm = false
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/registration/${registrationId}/move`,
      { target, confirm }
    ),
  replaceGroupPlacePair: (
    clubId: string,
    tournamentId: string,
    placeId: string,
    registrationId: string,
    confirm = false
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group-place/${placeId}/replace`,
      { registrationId, confirm }
    ),
  /** `result` reads from the side of `placeIds[0]`. */
  saveGroupMatchResult: (
    clubId: string,
    tournamentId: string,
    placeIds: [string, string],
    result: MatchResult,
    confirm = false
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group-match/${placeIds[0]}/${placeIds[1]}/result`,
      { result, confirm }
    ),
  deleteGroupMatchResult: (
    clubId: string,
    tournamentId: string,
    placeIds: [string, string],
    confirm = false
  ) =>
    api.delete<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group-match/${placeIds[0]}/${placeIds[1]}/result`,
      { confirm }
    ),
  /** The tied places, best first. */
  setGroupTieOrder: (
    clubId: string,
    tournamentId: string,
    groupId: string,
    placeIds: string[],
    confirm = false
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group/${groupId}/tie-order`,
      { placeIds, confirm }
    ),
  setBracketFillWithThirds: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    fillWithThirds: boolean
  ) =>
    api.patch<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket`,
      { fillWithThirds }
    ),
  setBracketTieOrder: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    groupRank: 1 | 2 | 3,
    placeIds: string[],
    confirm = false
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/tie-order`,
      { groupRank, placeIds, confirm }
    ),
  /** `result` reads from the side of the match's first slot. */
  saveBracketMatchResult: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    match: BracketMatchKey,
    result: MatchResult,
    confirm = false
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/match/${match.round}/${match.position}/result`,
      { result, confirm }
    ),
  deleteBracketMatchResult: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    match: BracketMatchKey,
    confirm = false
  ) =>
    api.delete<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/match/${match.round}/${match.position}/result`,
      { confirm }
    ),
  rebuildBracket: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    confirm = false
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/rebuild`,
      { confirm }
    ),
  swapBracketPlaces: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    slots: [FirstRoundPlace, FirstRoundPlace],
    confirm = false
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/swap`,
      { slots, confirm }
    ),
  pinBracketPair: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    place: BracketPlace,
    registrationId: string,
    confirm = false
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/pin`,
      { ...place, registrationId, confirm }
    ),
  resetBracketPlace: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    place: BracketPlace,
    confirm = false
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/automatic`,
      { ...place, confirm }
    ),
  clearDiscardedBracketAdjustments: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.delete<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/discarded`
    ),
  /** Books `courtId` for 1 hour from `start`; 409 when the court is taken. */
  setBracketMatchSchedule: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    match: BracketMatchKey,
    schedule: { courtId: string; start: Date }
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/match/${match.round}/${match.position}/schedule`,
      schedule
    ),
  getCourtBoard: (clubId: string, tournamentId: string) =>
    api.get<CourtBoard>(`/web-app/club/${clubId}/tournament/${tournamentId}/courts`),
  playOnCourt: (clubId: string, tournamentId: string, courtId: string, matchKey: string) =>
    api.post<CourtBoard>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/court/${courtId}/play`,
      { matchKey }
    ),
  queueOnCourt: (
    clubId: string,
    tournamentId: string,
    courtId: string,
    matchKey: string,
    index: number
  ) =>
    api.post<CourtBoard>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/court/${courtId}/queue`,
      { matchKey, index }
    ),
  resetCourt: (clubId: string, tournamentId: string, courtId: string) =>
    api.delete<CourtBoard>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/court/${courtId}/placements`
    ),
  removeBracketMatchSchedule: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    match: BracketMatchKey
  ) =>
    api.delete<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/bracket/match/${match.round}/${match.position}/schedule`
    ),
  placeWaitingRegistrations: (
    clubId: string,
    tournamentId: string,
    categoryId: string
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/place-waiting`
    ),
  shuffleTournamentGroups: (
    clubId: string,
    tournamentId: string,
    categoryId: string,
    confirm = false
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/category/${categoryId}/shuffle`,
      { confirm }
    ),
  getTournamentAvailability: (clubId: string, tournamentId: string) =>
    api.get<TournamentAvailability>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/availability`
    ),
  getCourtsAvailability: (clubId: string, startDate: string, endDate: string) =>
    api.get<TournamentAvailability>(
      `/web-app/club/${clubId}/tournament/availability?${new URLSearchParams({ startDate, endDate })}`
    ),
  addTournamentSlots: (clubId: string, tournamentId: string, range: SlotRangeInput) =>
    api.post<TournamentSchedule>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/slots`,
      range
    ),
  updateTournamentSlot: (
    clubId: string,
    tournamentId: string,
    slotId: string,
    slot: SlotInput
  ) =>
    api.patch<TournamentSchedule>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/slot/${slotId}`,
      slot
    ),
  removeTournamentSlot: (clubId: string, tournamentId: string, slotId: string) =>
    api.delete<TournamentSchedule>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/slot/${slotId}`
    ),
  /** Exactly the categories that play in the franja. */
  setTournamentSlotCategories: (
    clubId: string,
    tournamentId: string,
    slotId: string,
    categoryIds: string[]
  ) =>
    api.put<TournamentSchedule>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/slot/${slotId}/categories`,
      { categoryIds }
    ),
  /** Exactly the franjas the group plays in (0 to 2). */
  setGroupSlots: (
    clubId: string,
    tournamentId: string,
    groupId: string,
    slotIds: string[]
  ) =>
    api.put<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group/${groupId}/slots`,
      { slotIds }
    ),
  addGroupSlot: (
    clubId: string,
    tournamentId: string,
    groupId: string,
    slot: SlotInput
  ) =>
    api.post<CategoryStructure>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/group/${groupId}/slot`,
      slot
    ),
  getCourtById:(clubId: string, courtId: string) =>
    api.get(`/web-app/club/${clubId}/court/${courtId}`),

  // Public API
  getPublicTournamentById: (tournamentId: string) =>
    api.get<PublicTournament>(`/web-app/public/tournament/${tournamentId}`),
  createOpenMatch: (clubId: string, data: CreateOpenMatch) =>
    api.post<WebOpenMatch>(`/web-app/club/${clubId}/open-match`, data),
  getOpenMatches: (clubId: string) =>
    api.get<ApiResponseList<WebOpenMatch>>(
      `/web-app/club/${clubId}/open-match`
    ),
  updateOpenMatch: (
    clubId: string,
    reservationId: string,
    data: UpdateOpenMatch
  ) =>
    api.patch<WebOpenMatch>(
      `/web-app/club/${clubId}/open-match/${reservationId}`,
      data
    ),
  deleteOpenMatch: (clubId: string, reservationId: string) =>
    api.delete(`/web-app/club/${clubId}/open-match/${reservationId}`),
  getPublicOpenMatch: (reservationId: string) =>
    api.get<PublicOpenMatch>(`/web-app/public/open-match/${reservationId}`),
  joinOpenMatch: (reservationId: string, data: JoinOpenMatch) =>
    api.post<PublicOpenMatch>(
      `/web-app/public/open-match/${reservationId}/join`,
      data
    ),
};

export const courtApi = {
  getReservations: (clubId: string, courtId: string, date?: string) =>
    api.get<{ results: Reservation[] }>(
      `/web-app/club/${clubId}/court/${courtId}/reservation${
        date ? `?date=${date}` : ""
      }`
    ),
  getAll: (clubId: string) =>
    api.get<{ results: Court[]; count: number }>(
      `/web-app/club/${clubId}/court`
    ),
  create: (clubId: string, court: CreateCourt) =>
    api.post(`/web-app/club/${clubId}/court`, court),
  delete: (clubId: string, courtId: string) =>
    api.delete(`/web-app/club/${clubId}/court/${courtId}`),
};

export const categoryApi = {
  getAll: () => api.get<ApiResponseList<Category>>("/web-app/category"),
};

export const pointOfSaleApi = {
  getAll: (clubId: string) =>
    api.get<ApiResponseList<PointOfSale>>(`/web-app/club/${clubId}/pos`),
  create: (clubId: string, pointOfSale: Pick<PointOfSale, "name" | "description">) =>
    api.post(`/web-app/club/${clubId}/pos`, pointOfSale),
  getStartupData: () => api.get<{ club: Club; pos: PointOfSale }>("/pos"),
  updatePointOfSale: (
    clubId: string,
    posId: string,
    pointOfSale: Partial<PointOfSale>
  ) => api.patch(`/web-app/club/${clubId}/pos/${posId}`, pointOfSale),
};

export const playerApi = {
  getPlayers: (clubId: string, pagination: Pagination = ALL_PAGINATION) =>
    api.get<ApiResponseList<ClubListPlayer>>(
      `/web-app/club/${clubId}/player?${toUrlSearchParams(pagination)}`
    ),
  uploadAvatar: (clubId: string, playerId: string, avatar: FormData) =>
    api.post<ClubListPlayer>(`/web-app/club/${clubId}/player/${playerId}/avatar`, avatar),
  createPlayer: (clubId: string, player: CreatePlayer) =>
    api.post<Player>(`/web-app/club/${clubId}/player`, player),
  searchByPhone: (clubId: string, phone: string) =>
    api.get<ApiResponseList<Player>>(
      `/web-app/club/${clubId}/player/search?phone=${phone}`
    ),
  registerPlayer: (clubId: string, playerId: string) =>
    api.post(`/web-app/club/${clubId}/player/${playerId}`),
  searchPlayer: (query: string) =>
    api.get<ApiResponseList<Player>>(`/pos/player/search?query=${query}`),
  createPlayerPos: (player: CreatePlayer) => api.post(`/pos/player`, player),
};

export const pushSubscriptionApi = {
  getVapidPublicKey: () =>
    api.get<{ publicKey: string | null }>("/web-app/push-subscription/vapid-public-key"),
  subscribe: (subscription: PushSubscriptionJSON) =>
    api.post("/web-app/push-subscription", subscription),
  unsubscribe: (endpoint: string) =>
    api.delete("/web-app/push-subscription", { endpoint }),
};

const screenPath = (clubId: string, screenId = "") =>
  `/web-app/club/${clubId}/screen${screenId && `/${screenId}`}`;

/** Slides go as a form: an image slide carries its file. */
export const screenApi = {
  getAll: (clubId: string) =>
    api.get<ApiResponseList<ScreenSummary>>(screenPath(clubId)),
  get: (clubId: string, screenId: string) =>
    api.get<ScreenDetail>(screenPath(clubId, screenId)),
  create: (clubId: string, name: string) =>
    api.post<ScreenDetail>(screenPath(clubId), { name }),
  update: (
    clubId: string,
    screenId: string,
    changes: Partial<Pick<ScreenDetail, "name" | "rotation">>
  ) => api.patch<ScreenDetail>(screenPath(clubId, screenId), changes),
  delete: (clubId: string, screenId: string) =>
    api.delete(screenPath(clubId, screenId)),
  preview: (clubId: string, screenId: string) =>
    api.get<ScreenFeed>(`${screenPath(clubId, screenId)}/preview`),
  addSlide: (clubId: string, screenId: string, slide: FormData) =>
    api.post<ScreenDetail>(`${screenPath(clubId, screenId)}/slide`, slide),
  updateSlide: (clubId: string, screenId: string, slideId: string, slide: FormData) =>
    api.patch<ScreenDetail>(`${screenPath(clubId, screenId)}/slide/${slideId}`, slide),
  setSlideEnabled: (clubId: string, screenId: string, slideId: string, enabled: boolean) =>
    api.put<ScreenDetail>(`${screenPath(clubId, screenId)}/slide/${slideId}/enabled`, {
      enabled,
    }),
  deleteSlide: (clubId: string, screenId: string, slideId: string) =>
    api.delete<ScreenDetail>(`${screenPath(clubId, screenId)}/slide/${slideId}`),
  reorderSlides: (clubId: string, screenId: string, slideIds: string[]) =>
    api.put<ScreenDetail>(`${screenPath(clubId, screenId)}/slide-order`, { slideIds }),
  pairDevice: (clubId: string, screenId: string, code: string) =>
    api.post<ScreenDetail>(`${screenPath(clubId, screenId)}/device`, { code }),
  unpairDevice: (clubId: string, screenId: string, deviceId: string) =>
    api.delete<ScreenDetail>(`${screenPath(clubId, screenId)}/device/${deviceId}`),
  /** Its TVs show the tournament's awards slide until `endTakeover`. */
  takeOver: (clubId: string, screenId: string, takeover: ScreenTakeoverInput) =>
    api.put<ScreenDetail>(`${screenPath(clubId, screenId)}/takeover`, takeover),
  endTakeover: (clubId: string, screenId: string) =>
    api.delete<ScreenDetail>(`${screenPath(clubId, screenId)}/takeover`),
};

export type ScreenTakeoverInput = {
  type: ScreenTakeoverType;
  tournamentId: string;
  /** CHAMPIONS and RUNNERS_UP. */
  categoryId?: string;
};

/** Sponsors go as a form: the logo travels as a file. */
export const sponsorApi = {
  getAll: (clubId: string) =>
    api.get<ApiResponseList<Sponsor>>(`/web-app/club/${clubId}/sponsor`),
  create: (clubId: string, sponsor: FormData) =>
    api.post<Sponsor>(`/web-app/club/${clubId}/sponsor`, sponsor),
  update: (clubId: string, sponsorId: string, changes: FormData) =>
    api.patch<Sponsor>(`/web-app/club/${clubId}/sponsor/${sponsorId}`, changes),
  setTournamentSponsors: (clubId: string, tournamentId: string, sponsorIds: string[]) =>
    api.put<ApiResponseList<Sponsor>>(
      `/web-app/club/${clubId}/tournament/${tournamentId}/sponsors`,
      { sponsorIds }
    ),
};

/** What a TV calls; failures throw `ScreenDeviceRequestError` with the status. */
export const screenDeviceApi = {
  requestPairingCode: () =>
    screenDeviceRequest<ScreenPairingCode>("POST", "/web-app/screen-device/pairing"),
  getPairingStatus: (pairingToken: string) =>
    screenDeviceRequest<ScreenPairingStatus>("POST", "/web-app/screen-device/pairing/status", {
      body: { pairingToken },
    }),
  getFeed: (credential: string) =>
    screenDeviceRequest<ScreenFeed>("GET", "/web-app/screen-device/feed", { credential }),
};
